import {Info} from "lucide-react";

import {cn} from "@/lib/utils";
import {formatDate} from "@/src/modules/core/lib/formatters";
import {normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";

import {
  DispatchDeskCardShell,
  DispatchDeskEmptyMessage,
  DispatchDeskEmptyPanelBody,
  DispatchDeskIconValueRow,
  DispatchDeskLabelRow,
  DispatchDeskLabeledField,
  DispatchDeskPhoneRow,
  DispatchDeskScrollRegion,
  DispatchDeskSection,
  DISPATCH_DESK_GENERAL_INFO_ICONS,
  DISPATCH_DESK_NESTED_INDENT_CLASS,
} from "./dispatch-desk-card";
import {useDispatchDeskProperty} from "../hooks/use-dispatch-desk-property";
import {
  formatDispatchHouseholdLine,
  formatDispatchPropertyLine,
  formatDispatchResidentLine,
} from "../lib/dispatch-desk-general-info";
import {
  DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS,
  DISPATCH_DESK_MOBILE_COMPACT_PANEL_CAP_CLASS,
  DISPATCH_DESK_NO_PROPERTY_MESSAGE,
  DISPATCH_DESK_PANEL_CLASS,
} from "../lib/dispatch-desk-layout";
import {useDispatchDeskHouseholdMatch} from "../store/desk-household-match";

type DispatchPropertyGeneralInfoProps = {
  className?: string;
};

export function DispatchPropertyGeneralInfo({className}: DispatchPropertyGeneralInfoProps) {
  const property = useDispatchDeskProperty();
  const householdMatch = useDispatchDeskHouseholdMatch();
  const showHouseholdMatch =
    householdMatch != null &&
    property != null &&
    String(householdMatch.property_id) === String(property.id);

  return (
    <DispatchDeskCardShell
      className={cn(
        DISPATCH_DESK_PANEL_CLASS,
        DISPATCH_DESK_MOBILE_COMPACT_PANEL_CAP_CLASS,
        className,
      )}
      contentClassName={DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS}
      icon={Info}
      iconClassName="text-primary"
      title="General information"
    >
      <DispatchDeskScrollRegion capped>
        {!property ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>{DISPATCH_DESK_NO_PROPERTY_MESSAGE}</DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : (
          <GeneralInfoContent
            property={property}
            showHouseholdMatch={showHouseholdMatch}
            householdMatch={showHouseholdMatch ? householdMatch : null}
          />
        )}
      </DispatchDeskScrollRegion>
    </DispatchDeskCardShell>
  );
}

function GeneralInfoContent({
  property,
  showHouseholdMatch,
  householdMatch,
}: {
  property: NonNullable<ReturnType<typeof useDispatchDeskProperty>>;
  showHouseholdMatch: boolean;
  householdMatch: ReturnType<typeof useDispatchDeskHouseholdMatch>;
}) {
  const propertyLine = formatDispatchPropertyLine(property);
  const householdLine =
    showHouseholdMatch && householdMatch ? formatDispatchHouseholdLine(householdMatch) : null;
  const residentLine =
    showHouseholdMatch && householdMatch ? formatDispatchResidentLine(householdMatch) : null;

  const hasTelephone = Boolean(property.telephone && normalizeUsPhoneDigits(property.telephone));
  const hasSitePhone = Boolean(
    property.site_telephone && normalizeUsPhoneDigits(property.site_telephone),
  );
  const showValidFrom = Boolean(property.valid_from);
  const showValidTo = Boolean(property.valid_to);

  return (
    <div className={cn("flex flex-col gap-2", DISPATCH_DESK_NESTED_INDENT_CLASS)}>
      <DispatchDeskLabelRow label="Property" align="start">
        <DispatchDeskIconValueRow
          value={propertyLine}
          icon={DISPATCH_DESK_GENERAL_INFO_ICONS.property}
          iconLabel="Property"
          wrap
        />
      </DispatchDeskLabelRow>

      {householdLine ? (
        <DispatchDeskLabelRow label="Household" align="start">
          <DispatchDeskIconValueRow
            value={householdLine}
            icon={DISPATCH_DESK_GENERAL_INFO_ICONS.household}
            iconLabel="Household"
            wrap
          />
        </DispatchDeskLabelRow>
      ) : null}

      {residentLine ? (
        <DispatchDeskLabelRow label="Resident">
          <DispatchDeskIconValueRow
            value={residentLine}
            icon={DISPATCH_DESK_GENERAL_INFO_ICONS.resident}
            iconLabel="Resident"
          />
        </DispatchDeskLabelRow>
      ) : null}

      {hasTelephone ? (
        <DispatchDeskLabelRow label="Telephone">
          <DispatchDeskPhoneRow
            value={property.telephone}
            callLabel="property telephone"
            size="sm"
          />
        </DispatchDeskLabelRow>
      ) : null}

      {hasSitePhone ? (
        <DispatchDeskLabelRow label="Site phone">
          <DispatchDeskPhoneRow
            value={property.site_telephone}
            callLabel="site telephone"
            size="sm"
          />
        </DispatchDeskLabelRow>
      ) : null}

      {showValidFrom ? (
        <DispatchDeskLabelRow label="Valid from">
          <span className="tabular-nums">{formatDate(property.valid_from!)}</span>
        </DispatchDeskLabelRow>
      ) : null}

      {showValidTo ? (
        <DispatchDeskLabelRow label="Valid to">
          <span className="tabular-nums">{formatDate(property.valid_to!)}</span>
        </DispatchDeskLabelRow>
      ) : null}

      {property.notes ? (
        <DispatchDeskSection>
          <DispatchDeskLabeledField label="Notes">
            <p className="whitespace-pre-wrap">{property.notes}</p>
          </DispatchDeskLabeledField>
        </DispatchDeskSection>
      ) : null}
    </div>
  );
}
